import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Member } from '../types';

export interface MagicLink {
  id: string;
  memberId: string;
  token: string;
  email: string;
  groupId?: string; // Pour les liens de groupe
  isUsed: boolean; // Gardé pour compatibilité, mais pas utilisé pour bloquer
  expiresAt: Date;
  createdAt: Date;
  usedAt?: Date; // Gardé pour compatibilité
  ipAddress?: string; // Gardé pour compatibilité
  lastUsedAt?: Date; // Nouvelle date de dernière utilisation
  lastUsedIpAddress?: string; // Nouvelle IP de dernière utilisation
}

export class MagicLinkService {
  private static collection = 'magic_links';

  // Générer un lien magique pour un membre
  static async generateMagicLink(memberEmail: string, email: string): Promise<string> {
    const token = this.generateSecureToken();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24); // Expire dans 24h

    const magicLinkData = {
      id: token,
      memberId: memberEmail, // Utiliser l'email comme memberId
      token,
      email,
      isUsed: false,
      expiresAt: Timestamp.fromDate(expiresAt),
      createdAt: Timestamp.now()
    };

    await setDoc(doc(db, this.collection, token), magicLinkData);
    
    // Construire l'URL du lien magique
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000';
    return `${baseUrl}/vote/${token}`;
  }

  // Générer un lien magique pour un groupe d'élections
  static async generateGroupMagicLink(memberEmail: string, email: string, groupId: string): Promise<string> {
    const token = this.generateSecureToken();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 3); // Expire dans 3 heures

    const magicLinkData = {
      id: token,
      memberId: memberEmail, // Utiliser l'email comme memberId
      token,
      email,
      groupId,
      isUsed: false,
      expiresAt: Timestamp.fromDate(expiresAt),
      createdAt: Timestamp.now()
    };

    await setDoc(doc(db, this.collection, token), magicLinkData);
    
    // Construire l'URL du lien magique pour le groupe
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000';
    return `${baseUrl}/vote/group/${groupId}?token=${token}`;
  }

  // Valider et utiliser un lien magique
  static async validateAndUseMagicLink(token: string, ipAddress?: string): Promise<Member | null> {
    try {
      const magicLinkDoc = await getDoc(doc(db, this.collection, token));
      
      if (!magicLinkDoc.exists()) {
        throw new Error('Lien invalide');
      }

      const magicLinkData = magicLinkDoc.data();
      const magicLink: MagicLink = {
        ...magicLinkData,
        expiresAt: magicLinkData.expiresAt?.toDate() || new Date(),
        createdAt: magicLinkData.createdAt?.toDate() || new Date(),
        usedAt: magicLinkData.usedAt?.toDate(),
        lastUsedAt: magicLinkData.lastUsedAt?.toDate()
      } as MagicLink;

      // Vérifier si le lien a expiré
      const now = new Date();
      const expiresAt = magicLink.expiresAt;
      
      // Debug: afficher les dates
      console.log('Validation du lien magique:');
      console.log('Maintenant:', now.toISOString());
      console.log('Expire le:', expiresAt.toISOString());
      console.log('Lien expiré?', now > expiresAt);
      
      if (now > expiresAt) {
        throw new Error(`Lien expiré (expiré le ${expiresAt.toLocaleString()})`);
      }

      // Le lien peut être utilisé plusieurs fois, on ne vérifie pas isUsed
      // On met juste à jour la dernière utilisation pour le suivi
      const updateData: any = {
        lastUsedAt: Timestamp.now()
      };
      
      // Ajouter ipAddress seulement s'il est défini
      if (ipAddress) {
        updateData.lastUsedIpAddress = ipAddress;
      }
      
      // Mettre à jour la dernière utilisation (sans marquer comme "utilisé" définitivement)
      await updateDoc(doc(db, this.collection, token), updateData);

      // Récupérer les données du membre (memberId est maintenant l'email)
      const memberDoc = await getDoc(doc(db, 'members', magicLink.email));
      if (!memberDoc.exists()) {
        throw new Error('Membre non trouvé');
      }

      const data = memberDoc.data();
      return {
        id: data.email, // Utiliser l'email comme ID
        ...data,
        memberSince: data.memberSince?.toDate(),
        lastActive: data.lastActive?.toDate(),
      } as Member;

    } catch (error) {
      console.error('Erreur lors de la validation du lien magique:', error);
      throw error;
    }
  }

  // Générer un token sécurisé
  private static generateSecureToken(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Vérifier si un membre a déjà un lien magique actif
  static async hasActiveMagicLink(memberEmail: string): Promise<boolean> {
    // Cette fonction pourrait être implémentée pour vérifier les liens actifs
    // Pour l'instant, on permet toujours de générer un nouveau lien
    return false;
  }

  // Vérifier si un membre a déjà voté dans un groupe
  static async hasMemberVotedInGroup(memberEmail: string, groupId: string): Promise<boolean> {
    try {
      // Vérifier dans la collection des votes si le membre a déjà voté pour ce groupe
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const votesQuery = query(
        collection(db, 'votes'),
        where('memberId', '==', memberEmail), // Utiliser l'email comme memberId
        where('groupId', '==', groupId)
      );
      
      const votesSnapshot = await getDocs(votesQuery);
      return !votesSnapshot.empty;
    } catch (error) {
      console.error('Erreur lors de la vérification des votes:', error);
      return false;
    }
  }

  // Générer un lien magique individuel pour un membre (sans groupe spécifique)
  static async generateIndividualMagicLink(memberEmail: string, email: string): Promise<string> {
    const token = this.generateSecureToken();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 3); // Expire dans 3 heures

    // Debug: afficher les dates de génération
    console.log('Génération du lien magique:');
    console.log('Maintenant:', new Date().toISOString());
    console.log('Expire le:', expiresAt.toISOString());

    const magicLinkData = {
      id: token,
      memberId: memberEmail, // Utiliser l'email comme memberId
      token,
      email,
      // Pas de groupId pour un lien individuel
      isUsed: false,
      expiresAt: Timestamp.fromDate(expiresAt),
      createdAt: Timestamp.now()
    };

    await setDoc(doc(db, this.collection, token), magicLinkData);

    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000';
    return `${baseUrl}/vote/individual?token=${token}`;
  }

  // Obtenir les statistiques des liens magiques
  static async getMagicLinkStats(): Promise<{
    total: number;
    used: number;
    expired: number;
    active: number;
  }> {
    // Cette fonction pourrait être implémentée pour les statistiques
    return {
      total: 0,
      used: 0,
      expired: 0,
      active: 0
    };
  }
}
